import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { prascoProductsAPI, masterAPI } from '../lib/api';
import { formatDateShort } from '../lib/utils';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Boxes,
  Upload,
  Image as ImageIcon
} from 'lucide-react';

const ASSET_BASE_URL = (process.env.REACT_APP_ASSET_BASE_URL || process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const resolveImageUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return ASSET_BASE_URL ? `${ASSET_BASE_URL}${path}` : path;
};

const PrascoProducts = () => {
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Master data
  const [machineTypes, setMachineTypes] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [models, setModels] = useState([]);
  const [partNames, setPartNames] = useState([]);

  // Modal states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deletingProduct, setDeletingProduct] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    machine_type_id: '',
    manufacturer_id: '',
    model_id: '',
    part_name_id: '',
    part_number: '',
    is_featured: false,
    is_display: false,
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const pageSize = 10;

  useEffect(() => {
    fetchMasterData();
    fetchProducts();
  }, [page, search]);

  useEffect(() => {
    if (location.state?.openAdd) {
      openAddDialog();
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchMasterData = async () => {
    try {
      const [mtRes, mfRes, mdRes, pnRes] = await Promise.all([
        masterAPI.getAllMachineTypes(),
        masterAPI.getAllManufacturers(),
        masterAPI.getAllModels(),
        masterAPI.getAllPartNames(),
      ]);
      setMachineTypes(mtRes.data);
      setManufacturers(mfRes.data);
      setModels(mdRes.data);
      setPartNames(pnRes.data);
    } catch (error) {
      toast.error('Failed to load master data');
    }
  };

  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await prascoProductsAPI.getProducts(page, pageSize, search);
      setProducts(response.data.items);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch Prasco products');
    } finally {
      setIsLoading(false);
    }
  }, [page, search]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.machine_type_id || !formData.manufacturer_id ||
      !formData.model_id || !formData.part_name_id || !formData.part_number.trim()) {
      toast.error('Please fill all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      let productId;

      if (editingProduct) {
        await prascoProductsAPI.updateProduct(editingProduct.id, formData);
        productId = editingProduct.id;
        toast.success('Prasco product updated successfully');
      } else {
        const response = await prascoProductsAPI.createProduct(formData);
        productId = response.data.id;
        toast.success('Prasco product created successfully');
      }

      // Upload image if selected
      if (imageFile && productId) {
        try {
          await prascoProductsAPI.uploadImage(productId, imageFile);
        } catch (imgError) {
          toast.error('Product saved but image upload failed');
        }
      }

      setIsDialogOpen(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      const message = error.response?.data?.detail || 'Operation failed';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingProduct) return;

    try {
      await prascoProductsAPI.deleteProduct(deletingProduct.id);
      toast.success('Prasco product deleted successfully');
      setIsDeleteOpen(false);
      setDeletingProduct(null);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete Prasco product');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setFormData({
      machine_type_id: '',
      manufacturer_id: '',
      model_id: '',
      part_name_id: '',
      part_number: '',
      is_featured: false,
      is_display: false,
    });
    setImageFile(null);
    setImagePreview(null);
    setEditingProduct(null);
  };

  const openAddDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      machine_type_id: product.machine_type_id,
      manufacturer_id: product.manufacturer_id,
      model_id: product.model_id,
      part_name_id: product.part_name_id,
      part_number: product.part_number,
      is_featured: product.is_featured,
      is_display: product.is_display,
    });
    setImagePreview(resolveImageUrl(product.image_url));
    setIsDialogOpen(true);
  };

  const openDeleteDialog = (product) => {
    setDeletingProduct(product);
    setIsDeleteOpen(true);
  };

  return (
    <div className="animate-fade-in" data-testid="prasco-products-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-50 rounded-lg">
            <Boxes className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h1
              className="text-2xl font-bold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Prasco Products
            </h1>
            <p className="text-sm text-slate-500">{total} total products</p>
          </div>
        </div>
        <Button
          onClick={openAddDialog}
          className="bg-slate-900 hover:bg-slate-800"
          data-testid="add-prasco-product-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Prasco Product
        </Button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by part number or SKU..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-500">
            <Boxes className="w-12 h-12 mb-4 opacity-20" />
            <p>No Prasco products found</p>
            {search && (
              <Button
                variant="link"
                onClick={() => setSearch('')}
                className="mt-2"
              >
                Clear search
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>SKU</th>
                    <th>Part Number</th>
                    <th>Machine Type</th>
                    <th>Manufacturer</th>
                    <th>Model</th>
                    <th>Flags</th>
                    <th>Created</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product.id} data-testid={`prasco-product-row-${product.id}`}>
                      <td>
                        {product.image_url ? (
                          <img
                            src={resolveImageUrl(product.image_url)}
                            alt={product.part_number}
                            className="product-thumbnail"
                          />
                        ) : (
                          <div className="product-thumbnail flex items-center justify-center">
                            <ImageIcon className="w-5 h-5 text-slate-300" />
                          </div>
                        )}
                      </td>
                      <td className="font-mono text-xs">{product.sku}</td>
                      <td className="font-medium text-slate-900">{product.part_number}</td>
                      <td>{product.machine_type_name || '-'}</td>
                      <td>{product.manufacturer_name || '-'}</td>
                      <td>{product.model_name || '-'}</td>
                      <td>
                        <div className="flex flex-col gap-1">
                          {product.is_featured && (
                            <span className="badge badge-success">Featured</span>
                          )}
                          {product.is_display && (
                            <span className="badge badge-info">Display</span>
                          )}
                        </div>
                      </td>
                      <td>{formatDateShort(product.created_at)}</td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(product)}
                            data-testid={`edit-btn-${product.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeleteDialog(product)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-btn-${product.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                <p className="text-sm text-slate-500">
                  Page {page} of {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    data-testid="prev-page-btn"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    data-testid="next-page-btn"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsDialogOpen(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="prasco-product-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editingProduct ? 'Edit Prasco Product' : 'Add New Prasco Product'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
              {/* Machine Type */}
              <div className="space-y-2">
                <Label>Machine Type *</Label>
                <Select
                  value={String(formData.machine_type_id || "")}
                  onValueChange={(value) =>
                    setFormData({ ...formData, machine_type_id: Number(value) })
                  }
                >
                  <SelectTrigger data-testid="machine-type-select">
                    <SelectValue placeholder="Select machine type" />
                  </SelectTrigger>
                  <SelectContent>
                    {machineTypes.map((item) => (
                      <SelectItem key={item.id} value={String(item.id)}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Manufacturer */}
              <div className="space-y-2">
                <Label>Manufacturer *</Label>
                <Select
                  value={String(formData.manufacturer_id || "")}
                  onValueChange={(value) =>
                    setFormData({ ...formData, manufacturer_id: Number(value) })
                  }
                >
                  <SelectTrigger data-testid="manufacturer-select">
                    <SelectValue placeholder="Select manufacturer" />
                  </SelectTrigger>
                  <SelectContent>
                    {manufacturers.map((item) => (
                      <SelectItem key={item.id} value={String(item.id)}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Model */}
              <div className="space-y-2">
                <Label>Model *</Label>
                <Select
                  value={String(formData.model_id || "")}
                  onValueChange={(value) =>
                    setFormData({ ...formData, model_id: Number(value) })
                  }
                >
                  <SelectTrigger data-testid="model-select">
                    <SelectValue placeholder="Select model" />
                  </SelectTrigger>
                  <SelectContent>
                    {models.map((item) => (
                      <SelectItem key={item.id} value={String(item.id)}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Part Name */}
              <div className="space-y-2">
                <Label>Part Name *</Label>
                <Select
                  value={String(formData.part_name_id || "")}
                  onValueChange={(value) =>
                    setFormData({ ...formData, part_name_id: Number(value) })
                  }
                >
                  <SelectTrigger data-testid="part-name-select">
                    <SelectValue placeholder="Select part name" />
                  </SelectTrigger>
                  <SelectContent>
                    {partNames.map((item) => (
                      <SelectItem key={item.id} value={String(item.id)}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Part Number */}
              <div className="space-y-2">
                <Label>Part Number *</Label>
                <Input
                  value={formData.part_number}
                  onChange={(e) => setFormData({ ...formData, part_number: e.target.value })}
                  placeholder="Enter part number"
                  data-testid="part-number-input"
                />
              </div>

              {/* SKU (shown only when editing) */}
              {editingProduct && (
                <div className="space-y-2">
                  <Label>SKU (Auto-generated)</Label>
                  <Input
                    value={editingProduct.sku}
                    disabled
                    className="bg-slate-50"
                  />
                </div>
              )}

              {/* Flags */}
              <div className="space-y-4 md:col-span-2">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label>Featured Product</Label>
                    <p className="text-sm text-slate-500">Display on homepage featured section</p>
                  </div>
                  <Switch
                    checked={formData.is_featured}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
                    data-testid="is-featured-switch"
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label>Display Product</Label>
                    <p className="text-sm text-slate-500">Show in product catalog</p>
                  </div>
                  <Switch
                    checked={formData.is_display}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_display: checked })}
                    data-testid="is-display-switch"
                  />
                </div>
              </div>

              {/* Image Upload */}
              <div className="space-y-2 md:col-span-2">
                <Label>Product Image</Label>
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-6">
                  {imagePreview ? (
                    <div className="flex flex-col items-center gap-4">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="max-h-32 rounded-lg"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setImageFile(null);
                          setImagePreview(null);
                        }}
                      >
                        Remove Image
                      </Button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center gap-2 cursor-pointer">
                      <Upload className="w-8 h-8 text-slate-400" />
                      <span className="text-sm text-slate-500">Click to upload image</span>
                      <span className="text-xs text-slate-400">JPEG, PNG, WebP, GIF</span>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={handleImageChange}
                        className="hidden"
                        data-testid="image-upload-input"
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => { resetForm(); setIsDialogOpen(false); }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="save-prasco-product-btn"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : editingProduct ? (
                  'Update Product'
                ) : (
                  'Create Product'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Prasco Product?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the Prasco product "{deletingProduct?.part_number}" (SKU: {deletingProduct?.sku}). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PrascoProducts;
